import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/* ==========================================================================
   The Saved list (the mockup's HajjoWishlist, shared-overlays.js 1b)
   --------------------------------------------------------------------------
   Browser-side only: no account, no app, no sync between devices. One
   localStorage key, `hajjo_wishlist_v1`, holding a JSON array, newest first.
   An entry is a handle, as in the mockup:

       ["hajjo-oxford-black", "hajjo-derby-black"]

   or, when the piece was saved from a product page with a size chosen, a
   handle and that size:

       [{ "handle": "hajjo-oxford-black", "size": "42" }, "hajjo-derby-black"]

   Name, price and photograph are never stored; the Saved page reads them
   back from /products/<handle>.js, so a price change is never stale and a
   piece that has left the shop drops out.

   Every write dispatches a bubbling `hajjo:wishlist` event (from the control
   that caused it, else from document), detail { items, entries, action, handle }.
   ========================================================================== */

export const KEY = 'hajjo_wishlist_v1';
export const EVENT = 'hajjo:wishlist';

/**
 * @typedef {{ handle: string, size?: string }} WishlistEntry
 */

/**
 * Reads the list. A hand-edited or half-written key reads as an empty list rather than throwing.
 * @returns {WishlistEntry[]}
 */
function readEntries() {
  let raw = null;
  try {
    raw = JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch (error) {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  /** @type {Set<string>} */
  const seen = new Set();
  /** @type {WishlistEntry[]} */
  const out = [];

  for (const item of raw) {
    let handle = '';
    let size = '';
    if (typeof item === 'string' || typeof item === 'number') {
      handle = String(item).trim();
    } else if (item && typeof item === 'object' && typeof item.handle === 'string') {
      handle = item.handle.trim();
      if (typeof item.size === 'string' || typeof item.size === 'number') size = String(item.size).trim();
    }
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    out.push(size ? { handle, size } : { handle });
  }

  return out;
}

/**
 * @param {WishlistEntry[]} entries
 * @param {{ action: string, handle?: string, source?: EventTarget | null }} change
 * @returns {string[]} The handles, in saved order.
 */
function write(entries, { action, handle, source }) {
  const stored = entries.map((entry) => (entry.size ? { handle: entry.handle, size: entry.size } : entry.handle));
  try {
    localStorage.setItem(KEY, JSON.stringify(stored));
  } catch (error) {
    // Private mode or full storage: the page still updates for this visit.
  }
  announce(entries, action, handle, source);
  return entries.map((entry) => entry.handle);
}

/**
 * @param {WishlistEntry[]} entries
 * @param {string} action
 * @param {string} [handle]
 * @param {EventTarget | null} [source]
 */
function announce(entries, action, handle, source) {
  const target = source instanceof Node && source.isConnected ? source : document;
  target.dispatchEvent(
    new CustomEvent(EVENT, {
      bubbles: true,
      detail: { items: entries.map((entry) => entry.handle), entries, action, handle },
    })
  );
}

export const HajjoWishlist = {
  KEY,
  EVENT,

  /** @returns {WishlistEntry[]} */
  entries: readEntries,

  /** @returns {string[]} The saved handles, newest first. */
  read() {
    return readEntries().map((entry) => entry.handle);
  },

  /** @returns {string[]} */
  list() {
    return HajjoWishlist.read();
  },

  /** @param {string} handle */
  has(handle) {
    if (!handle) return false;
    return readEntries().some((entry) => entry.handle === String(handle));
  },

  /**
   * The size saved with a piece, if any.
   * @param {string} handle
   * @returns {string}
   */
  sizeOf(handle) {
    return readEntries().find((entry) => entry.handle === String(handle))?.size ?? '';
  },

  /**
   * Newest first: a piece saved now goes to the top of the Saved page.
   * @param {string} handle
   * @param {{ size?: string, source?: EventTarget | null }} [options]
   */
  add(handle, { size = '', source = null } = {}) {
    if (!handle) return HajjoWishlist.read();
    const key = String(handle);
    const entries = readEntries().filter((entry) => entry.handle !== key);
    entries.unshift(size ? { handle: key, size: String(size) } : { handle: key });
    return write(entries, { action: 'add', handle: key, source });
  },

  /**
   * @param {string} handle
   * @param {{ source?: EventTarget | null }} [options]
   */
  remove(handle, { source = null } = {}) {
    const key = String(handle);
    const entries = readEntries();
    if (!entries.some((entry) => entry.handle === key)) return entries.map((entry) => entry.handle);
    return write(
      entries.filter((entry) => entry.handle !== key),
      { action: 'remove', handle: key, source }
    );
  },

  /**
   * What every Save control calls.
   * @param {string} handle
   * @param {{ size?: string, source?: EventTarget | null }} [options]
   */
  toggle(handle, options = {}) {
    return HajjoWishlist.has(handle) ? HajjoWishlist.remove(handle, options) : HajjoWishlist.add(handle, options);
  },

  clear() {
    return write([], { action: 'clear' });
  },

  count() {
    return readEntries().length;
  },
};

// The same store for anything outside the module graph (and the console).
window.HajjoWishlist = HajjoWishlist;

/* ---------- The count beside SAVED in the header ----------
   Any element carrying `data-hajjo-saved-count` shows " (n)" once something is saved,
   as the header shows " (n)" beside Bag. */
function paintCounts() {
  const n = HajjoWishlist.count();
  for (const el of document.querySelectorAll('[data-hajjo-saved-count]')) {
    el.textContent = n > 0 ? ` (${n})` : '';
  }
}

document.addEventListener(EVENT, paintCounts);
// The header re-renders in the theme editor; paint the fresh spans.
document.addEventListener('shopify:section:load', paintCounts);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', paintCounts, { once: true });
} else {
  paintCounts();
}

// A list changed in another tab is the same list.
window.addEventListener('storage', (event) => {
  if (event.key === KEY) announce(readEntries(), 'sync');
});

/**
 * The size option of a product: the option named like "Size", else the only option.
 * @param {string[]} names
 * @returns {number} The option index, or -1.
 */
function sizeOptionIndex(names) {
  const index = names.findIndex((name) => /size/i.test(name));
  if (index !== -1) return index;
  return names.length === 1 ? 0 : -1;
}

/* ==========================================================================
   <hajjo-save-button>: the Save word on a card frame and beside the price
   on the product page (snippets/hajjo-save-button.liquid).
   ========================================================================== */

/**
 * @typedef {object} SaveButtonRefs
 * @property {HTMLButtonElement} button - The Save / Saved toggle.
 *
 * @extends {Component<SaveButtonRefs>}
 */
class HajjoSaveButton extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    document.addEventListener(EVENT, this.#paint, { signal: this.#abort.signal });
    this.#paint();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#paint();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * Toggles this piece on the Saved list. On a card the button sits over the card link,
   * so the click stops here: saving a piece never leaves the grid.
   * @param {Event} event
   */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    const handle = this.dataset.handle;
    if (!handle) return;
    HajjoWishlist.toggle(handle, { size: this.#chosenSize(), source: this });
  }

  /**
   * On the product page, the size the shopper has picked (if any) is saved with the piece.
   * @returns {string}
   */
  #chosenSize() {
    const option = this.dataset.sizeOption;
    if (!option) return '';
    const scope = this.closest('.shopify-section') ?? document;
    for (const input of scope.querySelectorAll('variant-picker input[type="radio"]:checked')) {
      if (input instanceof HTMLInputElement && input.dataset.optionName === option) return input.value;
    }
    return '';
  }

  #paint = () => {
    const { button } = this.refs;
    if (!button) return;
    const on = HajjoWishlist.has(this.dataset.handle ?? '');
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.classList.toggle('is-on', on);
    const label = on ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label && button.textContent?.trim() !== label) button.textContent = label;
  };
}

if (!customElements.get('hajjo-save-button')) customElements.define('hajjo-save-button', HajjoSaveButton);

/* ==========================================================================
   <hajjo-wishlist>: the Saved page (sections/hajjo-wishlist.liquid)
   ========================================================================== */

/**
 * @typedef {object} ProductJson
 * @property {number} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {number} price
 * @property {boolean} available
 * @property {string[]} options
 * @property {{ alt: string | null, src: string, media_type: string, width?: number, height?: number }[]} media
 * @property {string[]} images
 * @property {{ id: number, available: boolean, options: string[] }[]} variants
 */

/**
 * @typedef {object} WishlistRefs
 * @property {HTMLUListElement} grid - The card grid.
 * @property {HTMLTemplateElement} cardTemplate - One card, the server card's classes.
 * @property {HTMLElement} [note] - The line under the title.
 * @property {HTMLElement} [empty] - The empty state.
 * @property {HTMLElement} [showing] - "Showing all n pieces" under the grid.
 * @property {HTMLElement} [status] - Polite live region for adds and errors.
 *
 * @extends {Component<WishlistRefs>}
 */
class HajjoWishlistPage extends Component {
  requiredRefs = ['grid', 'cardTemplate'];

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Map<string, ProductJson | null>} */
  #products = new Map();
  #renderId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(EVENT, this.#onListChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {Event} event */
  #onListChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    // A piece removed here: take its card out without fetching anything.
    if (detail.action === 'remove' && detail.handle) {
      const card = this.refs.grid.querySelector(`[data-handle="${CSS.escape(detail.handle)}"]`);
      card?.remove();
      this.#paintCounts();
      return;
    }
    this.#render();
  };

  /** Fetches every saved piece and paints the grid in saved order. */
  async #render() {
    const renderId = ++this.#renderId;
    const handles = HajjoWishlist.read();
    const { grid } = this.refs;
    const signal = this.#abort?.signal;

    if (!handles.length) {
      grid.replaceChildren();
      this.#paintCounts();
      return;
    }

    grid.setAttribute('aria-busy', 'true');
    await Promise.all(handles.map((handle) => this.#fetchProduct(handle, signal)));
    if (renderId !== this.#renderId || signal?.aborted) return;

    const fragment = document.createDocumentFragment();
    for (const handle of HajjoWishlist.read()) {
      const product = this.#products.get(handle);
      if (product) fragment.append(this.#card(product));
    }
    grid.replaceChildren(fragment);
    grid.removeAttribute('aria-busy');
    this.#paintCounts();
  }

  /**
   * @param {string} handle
   * @param {AbortSignal | undefined} signal
   */
  async #fetchProduct(handle, signal) {
    if (this.#products.has(handle)) return;
    try {
      const response = await fetch(`${this.#root()}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        signal,
      });
      if (response.status === 404) {
        // The piece has left the shop: it drops off the list without a word.
        this.#products.set(handle, null);
        HajjoWishlist.remove(handle);
        return;
      }
      if (!response.ok) return;
      this.#products.set(handle, await response.json());
    } catch (error) {
      // Network trouble or an aborted render: leave it for the next render.
    }
  }

  #root() {
    const root = this.dataset.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  /** Note, empty state and the count line follow the list. */
  #paintCounts() {
    const n = this.refs.grid.children.length;
    const saved = HajjoWishlist.count();
    const { note, empty, showing } = this.refs;
    const isEmpty = saved === 0;

    if (empty) empty.hidden = !isEmpty;
    if (note) {
      const text = isEmpty ? this.dataset.noteEmpty : this.dataset.note;
      if (text) note.textContent = text;
    }
    if (showing) {
      const template = n === 1 ? this.dataset.showingOne : this.dataset.showingOther;
      showing.textContent = n && template ? template.replace('[count]', String(n)) : '';
    }
  }

  /**
   * One Limestone card, cloned from the section's template so the server card's classes
   * (and so its styles in hajjo-product-card-styles) apply.
   * @param {ProductJson} product
   * @returns {Node}
   */
  #card(product) {
    const fragment = /** @type {DocumentFragment} */ (this.refs.cardTemplate.content.cloneNode(true));
    const item = /** @type {HTMLElement} */ (fragment.firstElementChild);
    item.dataset.handle = product.handle;

    const url = product.url || `${this.#root()}products/${product.handle}`;
    for (const link of item.querySelectorAll('[data-slot="link"]')) {
      if (link instanceof HTMLAnchorElement) link.href = url;
    }
    const title = item.querySelector('[data-slot="title"]');
    if (title) title.textContent = product.title;

    const price = item.querySelector('[data-slot="price"]');
    if (price) price.textContent = this.#money(product.price);

    const images = (product.media ?? []).filter((media) => media.media_type === 'image' && media.src);
    this.#image(item.querySelector('[data-slot="packshot"]'), images[0], product.title);
    this.#image(item.querySelector('[data-slot="onmodel"]'), images[1], product.title);
    if (!images.length) item.querySelector('.card-gallery')?.classList.add('hajjo-saved__frame--empty');

    const remove = item.querySelector('[data-action="remove"]');
    if (remove) {
      remove.setAttribute('aria-label', (this.dataset.labelRemove ?? '').replace('[title]', product.title));
    }

    this.#buyState(item, product);
    return fragment;
  }

  /**
   * @param {Element | null} img
   * @param {{ alt: string | null, src: string } | undefined} media
   * @param {string} title
   */
  #image(img, media, title) {
    if (!(img instanceof HTMLImageElement)) return;
    if (!media) {
      img.remove();
      return;
    }
    const src = media.src.startsWith('//') ? `https:${media.src}` : media.src;
    const sized = (/** @type {number} */ width) => {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    };
    img.src = sized(800);
    img.srcset = [400, 600, 800, 1200, 1600].map((width) => `${sized(width)} ${width}w`).join(', ');
    img.alt = media.alt || title;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EGP';
    try {
      return formatMoney(cents, format, currency);
    } catch (error) {
      return String(cents / 100);
    }
  }

  /**
   * Sets up the buy row: sizes (hidden until asked for) and the Add to bag button.
   * @param {HTMLElement} item
   * @param {ProductJson} product
   */
  #buyState(item, product) {
    const button = item.querySelector('[data-action="add"]');
    const sizes = item.querySelector('[data-slot="sizes"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (!product.available) {
      button.disabled = true;
      button.textContent = this.dataset.labelSoldOut ?? button.textContent;
      sizes?.remove();
      return;
    }

    const index = sizeOptionIndex(product.options ?? []);
    const needsSize = product.variants.length > 1 && index !== -1;
    if (!needsSize || !(sizes instanceof HTMLElement)) {
      sizes?.remove();
      return;
    }

    sizes.setAttribute('aria-label', product.options[index] ?? '');
    const savedSize = HajjoWishlist.sizeOf(product.handle);
    /** @type {Map<string, boolean>} */
    const values = new Map();
    for (const variant of product.variants) {
      const value = variant.options[index];
      if (value === undefined) continue;
      values.set(value, Boolean(values.get(value)) || variant.available);
    }
    for (const [value, available] of values) {
      const size = document.createElement('button');
      size.type = 'button';
      size.className = 'hajjo-saved__size';
      size.dataset.action = 'size';
      size.dataset.value = value;
      size.textContent = value;
      size.setAttribute('aria-pressed', 'false');
      if (!available) {
        size.disabled = true;
        size.setAttribute('aria-label', `${value}, ${this.dataset.labelSoldOut ?? ''}`.replace(/, $/, ''));
      }
      sizes.append(size);
    }

    item.dataset.sizeIndex = String(index);
    // A size saved from the product page is chosen already, when it is still in stock.
    if (savedSize && values.get(savedSize)) {
      this.#chooseSize(item, savedSize);
      sizes.hidden = false;
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {string} value
   */
  #chooseSize(item, value) {
    item.dataset.size = value;
    for (const size of item.querySelectorAll('[data-action="size"]')) {
      if (size instanceof HTMLElement) size.setAttribute('aria-pressed', size.dataset.value === value ? 'true' : 'false');
    }
    const button = item.querySelector('[data-action="add"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.textContent = this.dataset.labelAdd ?? button.textContent;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(target instanceof HTMLButtonElement) || !this.contains(target)) return;
    const item = /** @type {HTMLElement | null} */ (target.closest('[data-handle]'));
    if (!item) return;
    const handle = item.dataset.handle ?? '';
    const action = target.dataset.action;

    event.preventDefault();

    if (action === 'remove') {
      this.#moveFocusFrom(item);
      HajjoWishlist.remove(handle, { source: target });
      return;
    }

    if (action === 'size') {
      this.#chooseSize(item, target.dataset.value ?? '');
      return;
    }

    if (action === 'add') this.#onAdd(item, target);
  };

  /**
   * Keeps focus in the grid when a card goes: on the next card's Remove, else the previous one's.
   * @param {HTMLElement} item
   */
  #moveFocusFrom(item) {
    const next = item.nextElementSibling ?? item.previousElementSibling;
    const focusTarget = next?.querySelector('[data-action="remove"]');
    if (focusTarget instanceof HTMLElement) {
      requestAnimationFrame(() => focusTarget.focus());
    } else {
      requestAnimationFrame(() => this.refs.empty?.querySelector('a')?.focus());
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLButtonElement} button
   */
  #onAdd(item, button) {
    const product = this.#products.get(item.dataset.handle ?? '');
    if (!product) return;

    const sizes = item.querySelector('[data-slot="sizes"]');
    let variant = null;

    if (sizes instanceof HTMLElement) {
      const value = item.dataset.size;
      if (!value) {
        // First press: show the sizes and wait for one, as the product page does.
        sizes.hidden = false;
        button.disabled = true;
        button.textContent = this.dataset.labelSelectSize ?? button.textContent;
        sizes.querySelector('button:not([disabled])')?.focus();
        return;
      }
      const index = Number(item.dataset.sizeIndex);
      variant =
        product.variants.find((candidate) => candidate.options[index] === value && candidate.available) ?? null;
    } else {
      variant = product.variants.find((candidate) => candidate.available) ?? null;
    }

    if (!variant) return;
    this.#addToBag(variant.id, button, item);
  }

  /**
   * Ajax add on Horizon's cart event contract, so the bag drawer opens and the header count follows.
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   * @param {HTMLElement} item
   */
  async #addToBag(variantId, button, item) {
    const sectionIds = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.push(component.dataset.sectionId);
    }

    const error = item.querySelector('[data-slot="error"]');
    if (error) error.textContent = '';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await this.#fetchCart();

      if (data.status) {
        button.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'hajjo-wishlist', itemCount: 1 },
        });
        this.#say(item, data.description || data.message || this.dataset.labelError || '');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'hajjo-wishlist',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
      if (this.refs.status) this.refs.status.textContent = Theme.translations.added;
      this.#openBag();
    } catch (failure) {
      deferred.reject(failure);
      button.dispatchEvent(
        new CartErrorEvent({
          error: failure instanceof Error ? failure.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#say(item, this.dataset.labelError ?? '');
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /**
   * The bag drawer opens on its own when the theme's "auto open" setting is on. The Saved page has
   * no fly-to-bag animation to show the add, so it opens the drawer itself when that setting is off.
   */
  #openBag() {
    const drawer = /** @type {(HTMLElement & { isOpen?: boolean, open?: () => void }) | null} */ (
      document.querySelector('theme-drawer#cart-drawer')
    );
    if (!drawer || drawer.querySelector('cart-drawer-component[auto-open]')) return;
    requestAnimationFrame(() => {
      if (!drawer.isOpen) drawer.open?.();
    });
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {HTMLElement} item
   * @param {string} message
   */
  #say(item, message) {
    const error = item.querySelector('[data-slot="error"]');
    if (error) error.textContent = message;
    if (this.refs.status) this.refs.status.textContent = message;
  }
}

if (!customElements.get('hajjo-wishlist')) customElements.define('hajjo-wishlist', HajjoWishlistPage);
